import React, { useCallback, useRef, useState } from 'react';
import API_CONFIG from '../../apiConfig';
import { useAuth } from '../../context/AuthContext';
import { tryonExamplePresets } from './examplePresets';

interface ClothingSelectionProps {
  imageUrls: string[];
  setImageUrls: React.Dispatch<React.SetStateAction<string[]>>;
  isExample?: boolean;
  onExampleSelect?: (clothingUrl: string, clothingId: number) => void;
}

// Use clothing examples from examplePresets.ts
const exampleClothing = tryonExamplePresets.map(preset => ({
  id: preset.id,
  url: preset.clothPreview,
  description: preset.name
}));

const ClothingSelection: React.FC<ClothingSelectionProps> = ({ imageUrls, setImageUrls, isExample = false, onExampleSelect }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentUser } = useAuth();

  const hasUploadedImages = imageUrls.length > 0;

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadStatus(null);
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('files', file);
    });
    formData.append('userId', currentUser?.uid || 'anonymous');

    try {
      const response = await fetch(API_CONFIG.TRYON_UPLOAD_URL, {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        setUploadStatus('Upload successful!');
        const data = await response.json();
        if (data.uploaded && Array.isArray(data.uploaded)) {
          setImageUrls(data.uploaded.map((item: { url: string }) => item.url));
        } else if (data.urls && Array.isArray(data.urls)) {
          setImageUrls(data.urls);
        } else if (data.url) {
          setImageUrls([data.url]);
        } else {
          setImageUrls([]);
        }
      } else {
        setUploadStatus('Upload failed.');
        setImageUrls([]);
      }
    } catch (error) {
      setUploadStatus('Upload failed.');
      setImageUrls([]);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleExampleClick = async (url: string) => {
    if (hasUploadedImages) return; // Disabled when images are uploaded
    try {
      setImageUrls([url]);
      if (onExampleSelect) {
        const exampleClothingItem = exampleClothing.find(item => item.url === url);
        if (exampleClothingItem) {
          onExampleSelect(url, exampleClothingItem.id);
        }
      }
    } catch (error) {
      console.error('Error loading example image:', error);
    }
  };

  return (
    <div className="bg-accent-sand rounded-lg p-4 h-full flex flex-col">
      <h3 className="text-lg font-medium text-text-heading mb-4 flex-shrink-0">Clothing Image</h3>
      <div className="space-y-4 flex-1 overflow-y-auto pr-2">
        {/* Upload area or preview */}
        {hasUploadedImages ? (
          <div className="bg-white rounded-lg p-4 border-2 border-dashed border-accent-taupe flex flex-col items-center justify-center relative h-[400px]">
            {/* Cross button to close preview and allow new upload */}
            <button
              className="absolute top-2 right-2 text-text-description hover:text-red-500 text-2xl focus:outline-none"
              onClick={() => {
                setImageUrls([]);
                setUploadStatus(null);
              }}
              aria-label="Close preview"
            >
              &times;
            </button>
            <h3 className="font-medium text-text-heading mb-3">
              {isExample ? 'Example Clothing' : `Uploaded Clothing Image${imageUrls.length > 1 ? 's' : ''}`}
            </h3>
            <div className="flex flex-col items-center justify-center w-full h-full">
              {imageUrls.map((url, idx) => (
                <div key={idx} className="flex justify-center items-center w-full h-full">
                  <img
                    src={url}
                    alt={`${isExample ? 'Example' : 'Uploaded'} clothing preview ${idx + 1}`}
                    className="w-4/5 object-contain rounded shadow-md max-h-[300px] bg-white"
                  />
                </div>
              ))}
            </div>
            {!isExample && uploadStatus && (
              <p className="text-green-600 mt-3 text-sm">Upload successful!</p>
            )}
            {isExample && (
              <p className="text-text-description mt-3 text-xs">This is an example image. Upload your own to customize.</p>
            )}
          </div>
        ) : (
          <div
            className="bg-accent-sand rounded-lg p-6 border-2 border-dashed border-accent-taupe flex flex-col items-center justify-center h-[400px]"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              accept="image/*"
            />
            <div className="upload-icon-container mb-4">
              <i className="fas fa-cloud-upload-alt text-secondary-600 text-2xl"></i>
            </div>
            <h3 className="font-medium text-text-heading mb-2">Upload Clothing Images</h3>
            <p className="text-sm text-text-description mb-4 text-center">Drag & drop clothing images or click to browse</p>
            {uploading ? (
              <div className="flex items-center justify-center h-12">
                <span className="inline-block w-8 h-8 border-4 border-secondary-500 border-t-transparent rounded-full animate-spin"></span>
                <span className="ml-2 text-sm text-text-description">Upload in progress...</span>
              </div>
            ) : (
              <button
                className="bg-secondary-500 hover:bg-secondary-600 text-white px-6 py-2 rounded-lg font-medium"
                onClick={handleButtonClick}
                disabled={uploading}
              >
                Select Files
              </button>
            )}
            <p className="text-xs text-text-description mt-3">JPG, PNG up to 10MB</p>
            {uploadStatus && (
              <p className={`mt-2 text-sm ${uploadStatus.includes('successful') ? 'text-green-600' : 'text-red-600'}`}>{uploadStatus}</p>
            )}
          </div>
        )}

        {/* Examples - always shown but greyed out when images uploaded */}
        <div className="mt-6">
          <h4 className={`text-sm font-medium mb-3 ${hasUploadedImages ? 'text-text-description' : 'text-text-heading'}`}>
            Or try an example:
          </h4>
          <div className="grid grid-cols-3 gap-3">
            {exampleClothing.map((item) => (
              <button
                key={item.id}
                onClick={() => handleExampleClick(item.url)}
                disabled={hasUploadedImages}
                className={`group relative rounded-lg overflow-hidden ${hasUploadedImages
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer'
                  }`}
              >
                <img
                  src={item.url}
                  alt={item.description}
                  className={`w-full h-32 object-cover transition-opacity ${hasUploadedImages
                      ? 'opacity-50 grayscale'
                      : 'group-hover:opacity-75'
                    }`}
                />
                <div className={`absolute inset-0 transition-opacity ${hasUploadedImages
                    ? 'bg-accent-sand0 bg-opacity-20'
                    : 'bg-black bg-opacity-0 group-hover:bg-opacity-20'
                  }`} />
                <p className={`absolute bottom-1 left-1 right-1 text-xs text-center rounded px-1 py-0.5 ${hasUploadedImages
                    ? 'text-text-description bg-accent-sand0 bg-opacity-50'
                    : 'text-white bg-black bg-opacity-50'
                  }`}>
                  {item.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClothingSelection; 